/**
 * Khối trên canvas Tự động hoá — thẻ trực quan kiểu bot-builder:
 *  - nhãn tab góc trên trái (đổi màu theo kết quả chạy thử),
 *  - thẻ trắng: biểu tượng tròn + tên loại khối, thân là xem trước nội dung
 *    (tin nhắn với biến {{…}} thành chip tím, luật điều kiện, thời gian chờ…),
 *  - nút "Bước tiếp theo ⊕" góc dưới phải = đầu ra (kéo dây hoặc bấm để thêm khối).
 * Khối Kích hoạt có dải màu chính + công tắc bật/tắt luồng + nút "Đổi kích hoạt".
 * Kèm 3 hộp chọn dạng lưới thẻ: loại kích hoạt · khối tiếp theo · mẫu luồng.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle2, Clock, GitBranch, LayoutGrid, PlusCircle, RefreshCw, Search, SkipForward, StickyNote, Workflow, XCircle,
  AlertTriangle, MousePointerClick, type LucideIcon,
} from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/misc'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { i18n } from '@/lib/i18n'
import { stageLabel } from '@/hooks/use-contacts'
import { useMessageTemplates } from '@/hooks/use-automation'
import { useOrgMembers } from '@/hooks/use-settings'
import { useChienDichKetBan } from '@/hooks/use-zalo-friend-campaigns'
import {
  ACTION_CHON_DUOC, ACTION_META, KHOI_DUNG_SAN, NGUONG_IM_LANG, NHOM_ACTION, NHOM_TRIGGER, TOAN_TU_KHONG_GIA_TRI, TOAN_TU_LABEL,
  TRIGGER_CHON_DUOC, TRIGGER_META, VONG_MAU, actionMeta, triggerMeta,
  type LuatDieuKien, type Mau, type NhomAction, type NhomTrigger,
} from '@/lib/automation/catalog'
import type { BlockData, KindKhoi, TrangThaiBuoc } from '@/lib/automation/flow-adapter'
import { moTaCho } from '@/lib/automation/run-result'
import { scheduleSummary } from '@/lib/automation/lich-chay'
import { MAU_WORKFLOW, NGANH, NHOM_MAU, type MauWorkflow, type Nganh, type NhomMau } from '@/lib/automation/mau-workflow'

// ── Ngữ cảnh hành vi canvas ──────────────────────────────────────────
export interface CanvasHanhVi {
  /** Bấm "Bước tiếp theo" trên một khối (handle 'true'/'false' với Điều kiện). */
  themSau: (nodeId: string, handle?: 'true' | 'false') => void
  doiKichHoat: () => void
  enabled: boolean
  toggle: (v: boolean) => void
  chiXem: boolean
}
export const CanvasCtx = createContext<CanvasHanhVi | null>(null)

export const KHOI_META: Record<KindKhoi, { nhan: string; icon: LucideIcon; mau: Mau }> = {
  source: { get nhan() { return i18n.t('autoCore:canvas.blockKind.source') }, icon: Workflow, mau: 'blue' },
  action: { get nhan() { return i18n.t('autoCore:canvas.blockKind.action') }, icon: Workflow, mau: 'emerald' },
  condition: { get nhan() { return i18n.t('autoCore:canvas.blockKind.condition') }, icon: GitBranch, mau: 'amber' },
  delay: { get nhan() { return i18n.t('autoCore:canvas.blockKind.delay') }, icon: Clock, mau: 'slate' },
  note: { get nhan() { return i18n.t('autoCore:canvas.blockKind.note') }, icon: StickyNote, mau: 'amber' },
}

// ── Mảnh hiển thị ────────────────────────────────────────────────────
export function BieuTuongBuoc({ status, className = 'h-4 w-4' }: { status: TrangThaiBuoc; className?: string }) {
  if (status === 'ok') return <CheckCircle2 className={cn(className, 'text-success')} />
  if (status === 'fail') return <XCircle className={cn(className, 'text-destructive')} />
  if (status === 'wait') return <Clock className={cn(className, 'text-warning')} />
  return <SkipForward className={cn(className, 'text-muted-foreground')} />
}

/** Văn bản có {{biến}} → chip tím (như trình dựng bot). */
export function HienBien({ text, className }: { text: string; className?: string }) {
  const parts = text.split(/(\{\{[^}]+\}\})/g)
  return (
    <span className={className}>
      {parts.map((p, i) => /^\{\{[^}]+\}\}$/.test(p)
        ? <span key={i} className="mx-px inline-block rounded bg-violet-100 px-1 font-mono text-[10.5px] leading-4 text-violet-700 dark:bg-violet-500/25 dark:text-violet-200">{p}</span>
        : <span key={i}>{p}</span>)}
    </span>
  )
}

export function VongIcon({ icon: I, mau, size = 'h-9 w-9' }: { icon: LucideIcon; mau: Mau; size?: string }) {
  return <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full', size, VONG_MAU[mau])}><I className="h-[45%] w-[45%]" /></span>
}

function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return <div className="flex items-baseline gap-2 text-[11px]"><span className="w-16 shrink-0 text-muted-foreground">{nhan}</span><span className="min-w-0 flex-1 truncate font-medium">{children}</span></div>
}

function KhungTin({ children }: { children: ReactNode }) {
  return <div className="line-clamp-4 whitespace-pre-wrap rounded-md border bg-muted/40 px-2.5 py-2 text-[12px] leading-5">{children}</div>
}

function Chip({ children, mau = 'bg-muted text-foreground' }: { children: ReactNode; mau?: string }) {
  return <span className={cn('inline-flex max-w-full items-center truncate rounded-full px-2 py-0.5 text-[10.5px] font-medium', mau)}>{children}</span>
}

function ChuaDat({ children }: { children: ReactNode }) {
  return <p className="text-[11px] italic text-muted-foreground">{children}</p>
}

export function LuatChip({ r }: { r: LuatDieuKien }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-[11px]">
      <span className="rounded bg-violet-100 px-1 font-mono text-[10.5px] text-violet-700 dark:bg-violet-500/25 dark:text-violet-200">{r.field || '…'}</span>
      <span className="text-muted-foreground">{TOAN_TU_LABEL[r.op] ?? r.op}</span>
      {!TOAN_TU_KHONG_GIA_TRI.has(r.op) && <span className="font-medium">“{r.value || '…'}”</span>}
    </div>
  )
}

/** Nút đầu ra "Bước tiếp theo ⊕" chứa handle nguồn của React Flow. */
function NutTiepTheo({ id, nhan, mau, onClick, chiXem }: { id?: string; nhan: string; mau: string; onClick: () => void; chiXem?: boolean }) {
  const { t } = useTranslation('autoCore')
  return (
    <div className={cn('nodrag relative flex items-center gap-1 rounded-md py-1 pl-2.5 pr-3 text-[11px] font-semibold text-white shadow-sm', mau)}>
      <button type="button" disabled={chiXem} className="flex items-center gap-1 hover:opacity-90 disabled:cursor-default" onClick={(e) => { e.stopPropagation(); onClick() }} title={t('canvas.nextHint')}>
        {nhan} {!chiXem && <PlusCircle className="h-3.5 w-3.5" />}
      </button>
      <Handle id={id} type="source" position={Position.Right} isConnectable={!chiXem} className="!h-3 !w-3 !border-2 !border-background !bg-foreground/80 hover:!bg-foreground" />
    </div>
  )
}

// ── Thân khối theo loại ──────────────────────────────────────────────
function ThanKichHoat({ d }: { d: BlockData }) {
  const { t } = useTranslation('autoCore')
  const m = triggerMeta(d.loai)
  const cfg = d.config
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <VongIcon icon={m.icon} mau={m.mau} size="h-7 w-7" />
        <div className="min-w-0"><p className="truncate text-[12.5px] font-semibold">{m.ten}</p><p className="line-clamp-2 text-[11px] text-muted-foreground">{m.moTa}</p></div>
      </div>
      {d.loai === 'schedule' && <Dong nhan={t('canvas.trigger.schedule')}>{scheduleSummary(cfg)}</Dong>}
      {d.loai === 'conversation_idle' && <Dong nhan={t('canvas.trigger.threshold')}>{NGUONG_IM_LANG.find((x) => x.value === (cfg.threshold ?? '30m'))?.label ?? String(cfg.threshold)}</Dong>}
      {d.loai === 'webhook' && <p className="text-[10.5px] text-muted-foreground">{t('canvas.trigger.webhookHint')}</p>}
      {d.loai === 'manual' && (
        <div className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-[12px] font-semibold text-primary">
          <MousePointerClick className="h-3.5 w-3.5" /> {String(cfg.buttonLabel || t('canvas.trigger.runFlow'))}
        </div>
      )}
      {((cfg.filterRules as LuatDieuKien[] | undefined) ?? []).map((r, i) => (
        <div key={i} className="flex items-center gap-1">
          <span className="shrink-0 text-[10.5px] text-muted-foreground">{i === 0 ? t('canvas.onlyIf') : cfg.filterLogic === 'or' ? t('canvas.or') : t('canvas.and')}</span><LuatChip r={r} />
        </div>
      ))}
      {m.khongCoNguonPhat && (
        <p className="flex items-start gap-1 text-[10.5px] text-warning"><AlertTriangle className="mt-px h-3 w-3 shrink-0" /> {t('canvas.trigger.deprecated')}</p>
      )}
    </div>
  )
}

function ThanDieuKien({ d }: { d: BlockData }) {
  const { t } = useTranslation('autoCore')
  const rules = (d.config.rules as LuatDieuKien[] | undefined) ?? []
  const or = d.config.logic === 'or'
  if (!rules.length) return <ChuaDat>{t('canvas.condition.noRules')}</ChuaDat>
  return (
    <div className="space-y-1">
      {rules.map((r, i) => (
        <div key={i} className="space-y-1">
          {i > 0 && <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{or ? t('canvas.or') : t('canvas.and')}</p>}
          <LuatChip r={r} />
        </div>
      ))}
    </div>
  )
}

function ThanCho({ d }: { d: BlockData }) {
  const { t } = useTranslation('autoCore')
  const dv = moTaCho(d.config.duration, d.config.unit).split(' ')
  return <p className="text-[20px] font-bold tabular-nums leading-6">{dv[0]} <span className="text-[13px] font-medium text-muted-foreground">{dv.slice(1).join(' ')}</span><span className="ml-2 text-[11px] font-normal text-muted-foreground">{t('canvas.delay.then')}</span></p>
}

function ThanHanhDong({ d }: { d: BlockData }) {
  const { t } = useTranslation('autoCore')
  const cfg = d.config
  const S = (k: string) => String(cfg[k] ?? '')
  const { data: templates } = useMessageTemplates(d.loai === 'send_template')
  const { data: members } = useOrgMembers(d.loai === 'assign_agent')
  switch (d.loai) {
    case 'send_message':
      return S('text') ? <KhungTin><HienBien text={S('text')} /></KhungTin> : <ChuaDat>{t('canvas.action.noText')}</ChuaDat>
    case 'send_template': {
      const tpl = templates?.find((x) => x.id === S('templateId'))
      if (!S('templateId')) return <ChuaDat>{t('canvas.action.noTemplate')}</ChuaDat>
      return <div className="space-y-1"><Dong nhan={t('canvas.action.template')}>{tpl?.name ?? S('templateId')}</Dong>{tpl && <KhungTin><HienBien text={tpl.content} /></KhungTin>}</div>
    }
    case 'send_zalo_zns': {
      const data = (cfg.templateData as Record<string, string> | undefined) ?? {}
      if (!S('accountId') || !S('templateId')) return <ChuaDat>{t('canvas.action.noZns')}</ChuaDat>
      return (
        <div className="space-y-1">
          <Dong nhan={t('canvas.action.znsTemplate')}>{S('templateId')}</Dong>
          {Object.keys(data).length > 0 && (
            <div className="rounded-md border bg-muted/40 px-2 py-1 font-mono text-[10.5px] leading-5">
              {Object.entries(data).slice(0, 3).map(([k, v]) => <div key={k} className="truncate">{k} = <HienBien text={String(v)} /></div>)}
            </div>
          )}
        </div>
      )
    }
    case 'add_tag': case 'remove_tag':
      return S('tag')
        ? <div className="flex flex-wrap items-center gap-1"><span className="text-[11px] text-muted-foreground">{d.loai === 'add_tag' ? t('canvas.action.tagAdd') : t('canvas.action.tagRemove')}</span><Chip mau={d.loai === 'add_tag' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200'}>{S('tag')}</Chip></div>
        : <ChuaDat>{t('canvas.action.noTag')}</ChuaDat>
    case 'update_lifecycle': case 'change_status':
      return <Dong nhan={t('canvas.action.moveTo')}>{S('lifecycleStage') ? stageLabel(S('lifecycleStage')) : '—'}</Dong>
    // Rule cũ lưu khoá `key`/`value` — backend vẫn đọc được nên hiển thị như nhau.
    case 'update_property':
      return S('fieldKey') || S('key') ? <Dong nhan={S('fieldKey') || S('key')}><HienBien text={S('value') || '…'} /></Dong> : <ChuaDat>{t('canvas.action.noProperty')}</ChuaDat>
    case 'increment_property':
      return S('fieldKey') || S('key') ? <Dong nhan={S('fieldKey') || S('key')}>+ {Number(cfg.amount ?? cfg.value ?? 1)}</Dong> : <ChuaDat>{t('canvas.action.noProperty')}</ChuaDat>
    case 'track_event':
      return S('eventName') ? <Dong nhan={t('canvas.action.event')}><span className="font-mono">{S('eventName')}</span>{S('eventValue') && <span className="text-muted-foreground"> · {S('eventValue')}</span>}</Dong> : <ChuaDat>{t('canvas.action.noEvent')}</ChuaDat>
    case 'assign_agent': {
      const m = members?.find((x) => x.id === S('userId'))
      return S('userId') ? <Dong nhan={t('canvas.action.assignTo')}>{m?.fullName ?? S('userId')}</Dong> : <ChuaDat>{t('canvas.action.noAgent')}</ChuaDat>
    }
    case 'create_appointment':
      return <div className="space-y-1"><KhungTin>{S('title') || t('canvas.action.defaultAppointmentTitle')}</KhungTin><p className="text-[10.5px] text-muted-foreground">{t('canvas.action.appointmentAfter', { hours: Number(cfg.offsetHours ?? 24) })}</p></div>
    case 'send_notification': {
      const nhan = S('recipients') === 'users' ? t('canvas.action.nUsers', { count: (cfg.userIds as string[] | undefined)?.length ?? 0 }) : S('recipients') === 'admins' ? t('canvas.action.admins') : t('canvas.action.assignee')
      return (
        <div className="space-y-1">
          <Dong nhan={t('canvas.action.notifyTo')}>{nhan}</Dong>
          {S('title') && <p className="truncate text-[12px] font-semibold"><HienBien text={S('title')} /></p>}
          {S('message') ? <KhungTin><HienBien text={S('message')} /></KhungTin> : !S('title') && <ChuaDat>{t('canvas.action.noContent')}</ChuaDat>}
        </div>
      )
    }
    case 'http_request':
      return S('url')
        ? <div className="space-y-1"><div className="flex items-center gap-1.5 text-[11px]"><span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] font-bold text-white">{S('method') || 'POST'}</span><span className="truncate font-mono"><HienBien text={S('url')} /></span></div>{S('body') && <pre className="line-clamp-3 whitespace-pre-wrap rounded-md border bg-muted/40 px-2 py-1 font-mono text-[10px] leading-4"><HienBien text={S('body')} /></pre>}</div>
        : <ChuaDat>{t('canvas.action.noUrl')}</ChuaDat>
    case 'send_friend_request':
      return S('campaignId') ? <ChienDichKetBanTen id={S('campaignId')} /> : <ChuaDat>{t('canvas.action.noCampaign')}</ChuaDat>
    case 'ai_cdp':
      return <p className="text-[11px] text-muted-foreground">{t('canvas.action.aiCdp')}</p>
    default:
      return <ChuaDat>{d.loai ? t('canvas.action.unsupported', { type: d.loai }) : t('canvas.action.noType')}</ChuaDat>
  }
}

function ChienDichKetBanTen({ id }: { id: string }) {
  const { t } = useTranslation('autoCore')
  const { data } = useChienDichKetBan()
  const c = data?.find((x) => x.id === id)
  return <div className="space-y-1"><Dong nhan={t('canvas.action.campaign')}>{c?.name ?? id}</Dong><p className="text-[10.5px] text-muted-foreground">{t('canvas.action.friendRequestHint')}</p></div>
}

// ── Node ─────────────────────────────────────────────────────────────
const TAB_MAU: Record<TrangThaiBuoc | 'none', string> = {
  none: 'bg-primary text-primary-foreground', ok: 'bg-success text-white', fail: 'bg-destructive text-white', wait: 'bg-warning text-white', skip: 'bg-muted-foreground/70 text-white',
}

export function KhoiCanvas({ id, data, selected }: NodeProps) {
  const d = data as BlockData
  const hv = useContext(CanvasCtx)
  const { t } = useTranslation('autoCore')
  const K = KHOI_META[d.kind]
  const laKichHoat = d.kind === 'source'
  const tm = laKichHoat ? triggerMeta(d.loai) : null
  const am = d.kind === 'action' ? actionMeta(d.loai) : null
  const HeadIcon = tm?.icon ?? am?.icon ?? K.icon
  const headMau: Mau = am?.mau ?? K.mau
  const tieuDe = am ? am.ten : K.nhan

  if (d.kind === 'note') {
    return (
      <div className={cn('w-60 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] shadow-sm dark:border-amber-500/40 dark:bg-amber-500/10', selected && 'ring-2 ring-primary/70')}>
        <Handle type="target" position={Position.Left} className="!h-2 !w-2 !bg-amber-400" />
        <p className="flex items-center gap-1.5 font-semibold"><StickyNote className="h-3.5 w-3.5" /> {d.label}</p>
        {typeof d.config.text === 'string' && <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{d.config.text}</p>}
        <Handle type="source" position={Position.Right} className="!h-2 !w-2 !bg-amber-400" />
      </div>
    )
  }

  return (
    <div className={cn('relative w-72 pt-3', (d.runStatus === 'skip' || d.status === 'disabled') && 'opacity-60')}>
      {!laKichHoat && <Handle type="target" position={Position.Left} isConnectable={!hv?.chiXem} className="!top-[calc(50%+6px)] !h-3 !w-3 !border-2 !border-background !bg-primary" />}
      {/* Nhãn tab */}
      <div className={cn('absolute left-3 top-0 z-10 flex max-w-[calc(100%-1.5rem)] items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold shadow-sm', TAB_MAU[d.runStatus ?? 'none'])}>
        {d.runStatus ? <BieuTuongBuoc status={d.runStatus} className="h-3 w-3 !text-white" /> : <K.icon className="h-3 w-3" />}
        <span className="truncate">{d.label}</span>
        {d.branch && <span className="ml-1 rounded bg-white/25 px-1 text-[10px]">{d.branch === 'true' ? t('canvas.true') : t('canvas.false')}</span>}
        {d.status === 'disabled' && <span className="ml-1 rounded bg-white/25 px-1 text-[10px]">{t('canvas.off')}</span>}
        {d.status === 'wip' && <span className="ml-1 rounded bg-white/25 px-1 text-[10px]">{t('canvas.wip')}</span>}
      </div>

      <div className={cn('overflow-visible rounded-xl border bg-card shadow-md transition-shadow', selected && 'ring-2 ring-primary/70', d.runStatus === 'fail' && 'border-destructive/60', d.runStatus === 'ok' && 'border-success/50')}>
        {laKichHoat ? (
          <div className="flex items-center gap-2 rounded-t-xl bg-primary px-3 pb-2.5 pt-4 text-primary-foreground">
            <HeadIcon className="h-4 w-4 shrink-0" />
            <p className="min-w-0 flex-1 truncate text-[13px] font-semibold">{t('canvas.header.trigger', { group: NHOM_TRIGGER[tm!.nhom].ten })}</p>
            {/* Không có quyền sửa → không hiện công tắc, chỉ ghi trạng thái. */}
            {hv?.chiXem
              ? <span className="shrink-0 rounded bg-primary-foreground/20 px-1.5 py-0.5 text-[10px] font-medium">{hv?.enabled ? t('canvas.on') : t('canvas.offNow')}</span>
              : <Switch className="nodrag" checked={!!hv?.enabled} onCheckedChange={(v) => hv?.toggle(v)} title={hv?.enabled ? t('canvas.flowOn') : t('canvas.flowOff')} />}
          </div>
        ) : (
          <div className="flex items-center gap-2.5 border-b px-3 pb-2 pt-4">
            <VongIcon icon={HeadIcon} mau={headMau} />
            <p className="min-w-0 flex-1 truncate text-[13.5px] font-semibold">{tieuDe}</p>
            {am?.sapCo && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{t('canvas.soon')}</span>}
          </div>
        )}

        <div className="px-3 py-2.5">
          {d.kind === 'source' && <ThanKichHoat d={d} />}
          {d.kind === 'condition' && <ThanDieuKien d={d} />}
          {d.kind === 'delay' && <ThanCho d={d} />}
          {d.kind === 'action' && <ThanHanhDong d={d} />}
        </div>

        {laKichHoat && !hv?.chiXem && (
          <div className="border-t px-3 py-2">
            <button type="button" className="nodrag flex w-full items-center justify-center gap-1.5 rounded-md border bg-muted/40 py-1.5 text-[11.5px] font-medium text-primary hover:bg-accent" onClick={(e) => { e.stopPropagation(); hv?.doiKichHoat() }}>
              <RefreshCw className="h-3.5 w-3.5" /> {t('canvas.changeTrigger')}
            </button>
          </div>
        )}

        {/* Đầu ra */}
        <div className={cn("flex px-2 pb-0 pt-1", d.kind === "condition" ? "-mb-3 flex-col items-end gap-1" : "-mb-3 justify-end gap-1.5")}>
          {d.kind === 'condition' ? (
            <>
              <NutTiepTheo id="true" nhan={t('canvas.true')} mau="bg-success" chiXem={hv?.chiXem} onClick={() => hv?.themSau(id, 'true')} />
              <NutTiepTheo id="false" nhan={t('canvas.false')} mau="bg-destructive" chiXem={hv?.chiXem} onClick={() => hv?.themSau(id, 'false')} />
            </>
          ) : <NutTiepTheo nhan={t('canvas.nextStep')} mau="bg-primary" chiXem={hv?.chiXem} onClick={() => hv?.themSau(id)} />}
        </div>
      </div>
    </div>
  )
}

// ── Hộp chọn dạng lưới thẻ ───────────────────────────────────────────
interface TheChon { key: string; ten: string; moTa: string; icon: LucideIcon; mau: Mau; nhom: string; tat?: string }

function LuoiChon({ nhom, the, onChon, tatCa, nhomMacDinh }: { nhom: { key: string; ten: string; icon?: LucideIcon }[]; the: TheChon[]; onChon: (t: TheChon) => void; tatCa: string; nhomMacDinh?: string }) {
  const { t } = useTranslation('autoCore')
  const [nh, setNh] = useState(nhomMacDinh ?? 'all')
  useEffect(() => { setNh(nhomMacDinh ?? 'all') }, [nhomMacDinh])
  const [q, setQ] = useState('')
  const hien = the.filter((x) => (nh === 'all' || x.nhom === nh) && (!q || `${x.ten} ${x.moTa}`.toLowerCase().includes(q.toLowerCase())))
  return (
    <div className="grid gap-4 sm:grid-cols-[11rem_1fr]">
      <nav className="flex gap-1 overflow-x-auto sm:block sm:space-y-0.5">
        {[{ key: 'all', ten: tatCa, icon: LayoutGrid }, ...nhom].map((n) => { const I = n.icon; return (
          <button key={n.key} type="button" onClick={() => setNh(n.key)} className={cn('flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-left text-[13px] sm:w-full', nh === n.key ? 'bg-primary/10 font-semibold text-primary' : 'hover:bg-accent')}>
            {I && <I className="h-4 w-4" />}{n.ten}
          </button>
        ) })}
      </nav>
      <div className="min-w-0 space-y-3">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('canvas.picker.search')} className="h-8 pl-8 text-xs" /></div>
        <div className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto pr-1 md:grid-cols-3">
          {hien.map((x) => (
            <button key={x.key} type="button" disabled={!!x.tat} onClick={() => onChon(x)} title={x.tat}
              className="group flex flex-col items-center gap-2 rounded-xl border bg-card px-3 py-5 text-center transition hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50">
              <VongIcon icon={x.icon} mau={x.mau} size="h-14 w-14" />
              <span className="text-[13px] font-semibold">{x.ten}</span>
              <span className="text-[11px] leading-4 text-muted-foreground">{x.tat ?? x.moTa}</span>
            </button>
          ))}
          {hien.length === 0 && <p className="col-span-full py-8 text-center text-xs text-muted-foreground">{t('canvas.picker.noItems')}</p>}
        </div>
      </div>
    </div>
  )
}

/** Chọn loại kích hoạt → trả về mã trigger. */
export function ChonKichHoatDialog({ open, onClose, onChon, hienTai }: { open: boolean; onClose: () => void; onChon: (trigger: string) => void; hienTai?: string }) {
  const { t } = useTranslation('autoCore')
  const the = useMemo<TheChon[]>(() => TRIGGER_CHON_DUOC.map((k) => {
    const m = TRIGGER_META[k]
    return { key: k, ten: m.ten, moTa: m.moTa, icon: m.icon, mau: m.mau, nhom: m.nhom }
  }), [])
  const nhom = (Object.keys(NHOM_TRIGGER) as NhomTrigger[]).map((k) => ({ key: k, ten: NHOM_TRIGGER[k].ten, icon: NHOM_TRIGGER[k].icon }))
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle>{t('canvas.pickTrigger.title')}</DialogTitle><DialogDescription>{t('canvas.pickTrigger.desc')}</DialogDescription></DialogHeader>
        <LuoiChon nhom={nhom} the={the} tatCa={t('canvas.pickTrigger.all')} nhomMacDinh={hienTai ? triggerMeta(hienTai).nhom : undefined} onChon={(x) => { onChon(x.key); onClose() }} />
      </DialogContent>
    </Dialog>
  )
}

export type KhoiMoi = { kind: 'condition' | 'delay' } | { kind: 'action'; actionType: string; config?: Record<string, unknown>; label?: string }

/** Chọn khối tiếp theo (hành động / điều kiện / chờ). */
export function ChonKhoiDialog({ open, onClose, onChon }: { open: boolean; onClose: () => void; onChon: (k: KhoiMoi) => void }) {
  const { t } = useTranslation('autoCore')
  const the = useMemo<(TheChon & { khoi: KhoiMoi })[]>(() => [
    ...Object.keys(ACTION_META).filter((a) => !ACTION_META[a].cu).map((a) => {
      const m = ACTION_META[a]
      return { key: a, nhom: m.nhom, ten: m.ten, moTa: m.moTa, icon: m.icon, mau: m.mau, get tat() { return m.sapCo ? i18n.t('autoCore:canvas.soon') : undefined }, khoi: { kind: 'action' as const, actionType: a } }
    }),
    ...KHOI_DUNG_SAN.map((k) => ({ key: k.key, nhom: k.nhom, ten: k.ten, moTa: k.moTa, icon: k.icon, mau: k.mau, khoi: { kind: 'action' as const, actionType: k.actionType, config: k.config, label: k.ten } })),
    { key: 'condition', nhom: 'luong', get ten() { return i18n.t('autoCore:canvas.blockKind.condition') }, get moTa() { return i18n.t('autoCore:canvas.pickBlock.conditionDesc') }, icon: GitBranch, mau: 'amber', khoi: { kind: 'condition' } },
    { key: 'delay', nhom: 'luong', get ten() { return i18n.t('autoCore:canvas.blockKind.delay') }, get moTa() { return i18n.t('autoCore:canvas.pickBlock.delayDesc') }, icon: Clock, mau: 'slate', khoi: { kind: 'delay' } },
  ], [])
  const nhom = (Object.keys(NHOM_ACTION) as (NhomAction | 'luong')[]).map((k) => ({ key: k, ten: NHOM_ACTION[k].ten, icon: NHOM_ACTION[k].icon }))
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle>{t('canvas.pickBlock.title')}</DialogTitle><DialogDescription>{t('canvas.pickBlock.desc')}</DialogDescription></DialogHeader>
        <LuoiChon nhom={nhom} the={the} tatCa={t('canvas.pickBlock.all')} onChon={(x) => { onChon((x as TheChon & { khoi: KhoiMoi }).khoi); onClose() }} />
      </DialogContent>
    </Dialog>
  )
}

/** Thư viện mẫu luồng — chọn một mẫu là áp lên canvas (hoặc tạo luồng mới). */
export function ChonMauDialog({ open, onClose, onChon, canhBao }: { open: boolean; onClose: () => void; onChon: (mau: MauWorkflow) => void; canhBao?: string }) {
  const { t } = useTranslation('autoCore')
  const [nh, setNh] = useState<NhomMau | 'all'>('all')
  const [ng, setNg] = useState<Nganh | 'all'>('all')
  const [q, setQ] = useState('')
  const theoNganh = (m: MauWorkflow) => ng === 'all' || !m.nganh?.length || m.nganh.includes(ng)
  const hien = MAU_WORKFLOW.filter((m) => (nh === 'all' || m.nhom === nh) && theoNganh(m) && (!q || `${m.ten} ${m.moTa} ${m.khiNao}`.toLowerCase().includes(q.toLowerCase())))
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t('canvas.templates.title')}</DialogTitle>
          <DialogDescription>{t('canvas.templates.desc', { warning: canhBao ? ` — ${canhBao}` : '' })}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground">{t('canvas.templates.byIndustry')}</span>
          {([['all', t('canvas.templates.allIndustries')], ...(Object.keys(NGANH) as Nganh[]).map((k) => [k, NGANH[k].ten] as const)] as Array<readonly [string, string]>).map(([k, ten]) => (
            <button key={k} type="button" onClick={() => setNg(k as Nganh | 'all')} className={cn('shrink-0 rounded-full border px-2.5 py-1 text-[11.5px]', ng === k ? 'border-primary bg-primary/10 font-semibold text-primary' : 'hover:bg-accent')}>
              {ten}
            </button>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-[13rem_1fr]">
          <nav className="flex gap-1 overflow-x-auto sm:block sm:space-y-0.5">
            {([['all', t('canvas.templates.allTemplates')], ...(Object.keys(NHOM_MAU) as NhomMau[]).map((k) => [k, NHOM_MAU[k].ten] as const)] as Array<readonly [string, string]>).map(([k, ten]) => (
              <button key={k} type="button" onClick={() => setNh(k as NhomMau | 'all')} className={cn('shrink-0 rounded-md px-3 py-2 text-left text-[13px] sm:w-full', nh === k ? 'bg-primary/10 font-semibold text-primary' : 'hover:bg-accent')}>
                {ten}
                <span className="ml-1 text-[11px] font-normal text-muted-foreground">{k === 'all' ? MAU_WORKFLOW.filter(theoNganh).length : MAU_WORKFLOW.filter((m) => m.nhom === k && theoNganh(m)).length}</span>
              </button>
            ))}
          </nav>
          <div className="min-w-0 space-y-3">
            <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('canvas.templates.search')} className="h-8 pl-8 text-xs" /></div>
            <div className="grid max-h-[62vh] grid-cols-1 gap-3 overflow-y-auto pr-1 lg:grid-cols-2">
              {hien.map((m) => { const tm = triggerMeta(m.trigger); return (
                <button key={m.id} type="button" onClick={() => { onChon(m); onClose() }} className="group flex flex-col gap-1.5 rounded-xl border bg-card p-3 text-left transition hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-md">
                  <div className="flex items-start gap-2">
                    <VongIcon icon={tm.icon} mau={tm.mau} size="h-8 w-8" />
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-semibold leading-tight">{m.ten}</p>
                      <p className="text-[11px] text-muted-foreground">{NHOM_MAU[m.nhom].ten} · {t('canvas.templates.blockCount', { count: m.flow.nodes.length })}</p>
                      {!!m.nganh?.length && <p className="truncate text-[10.5px] text-primary/80">{m.nganh.map((k) => NGANH[k].ten).join(' · ')}</p>}
                    </div>
                  </div>
                  <p className="text-[11.5px] leading-4 text-muted-foreground">{m.moTa}</p>
                  <div className="mt-auto space-y-0.5 pt-1 text-[11px]">
                    <p className="flex gap-1.5"><span className="shrink-0 text-muted-foreground">{t('canvas.templates.when')}</span><span className="min-w-0 flex-1">{m.khiNao}</span></p>
                    <p className="flex gap-1.5"><span className="shrink-0 text-muted-foreground">{t('canvas.templates.result')}</span><span className="min-w-0 flex-1">{m.ketQua}</span></p>
                  </div>
                </button>
              ) })}
              {hien.length === 0 && <p className="col-span-full py-8 text-center text-xs text-muted-foreground">{t('canvas.templates.none')}</p>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Action thêm nhanh ở cột trái của trình dựng. */
export const HANH_DONG_NHANH = ['send_message', 'add_tag', 'update_lifecycle', 'create_appointment'].filter((a) => ACTION_CHON_DUOC.includes(a))
